import { type FC, useCallback, useMemo, useState } from 'react';

import { Skeleton as SkeletonUI } from '@mui/material';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { ButtonDropdown } from '@vakantiesnl/components/src/molecules/ButtonDropdown/ButtonDropdown';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { type FilterState, useFilterStore } from '@vakantiesnl/services/src/stores/filtersStore';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type MicroCopy } from '@vakantiesnl/types';
import { type SortingOrder } from '@vakantiesnl/types/src/search';

import { useStyles } from './FilterControls.style';
import { Typography } from '../../atoms/Typography';

type Props = {
	microCopies: MicroCopy;
	filters: FilterState;
	total: number;
	isLoading: boolean;
	toggleSortDrawer?: () => void;
};

type SortingOption = { title: string; value: SortingOrder };

export const FiltersControls: FC<Props> = ({ microCopies, filters, total, isLoading, toggleSortDrawer }) => {
	const [mobileSortOption, setMobileSortOption] = useState<SortingOrder>(filters.sort);

	const { classes, cx } = useStyles();

	const setSort = useFilterStore((s) => s.setSort);
	const { isDesktop } = useBreakpoints();

	const handleApplyButtonClick = useCallback(() => {
		toggleSortDrawer && toggleSortDrawer();
		setSort(mobileSortOption);
	}, [mobileSortOption, setSort, toggleSortDrawer]);

	const optionsToRender: SortingOption[] = useMemo(() => {
		const popularOption: SortingOption = { title: 'popular', value: 'popularity_desc' };
		const bestScoreOption: SortingOption = {
			title: 'bestScore',
			value: 'hotel_review_rating',
		};
		const priceOption: SortingOption = { title: 'priceDesc', value: 'price' };

		return [popularOption, priceOption, bestScoreOption];
	}, []);

	const handleSortOption = useCallback(
		(optionValue: string) => {
			const sortOption = optionsToRender.find((option) => option.value === optionValue);
			if (sortOption) {
				setSort(sortOption.value);
			}
		},
		[setSort, optionsToRender],
	);

	const options = useMemo(
		() =>
			optionsToRender.map((option) => {
				const label = microCopies[`filters.sorting.${option.title}.label`];
				const hint = microCopies[`filters.sorting.${option.title}.hint`];

				return {
					group: 'sort',
					name: label,
					label,
					value: option.value,
					hint,
				};
			}),
		[microCopies, optionsToRender],
	);

	const selectedOption = useMemo(
		() => options.find((option) => option.value === filters.sort),
		[filters.sort, options],
	);

	const renderControls = useCallback(() => {
		if (isLoading) {
			return (
				<div className={classes.totalSkeleton}>
					<SkeletonUI animation="wave" variant="rectangular" width="50px" />
					<SkeletonUI animation="wave" variant="rectangular" width="150px" />
				</div>
			);
		}
		if (total === 0) {
			return (
				<Typography as="p" variant="labelMd">
					{total} {microCopies['common.accommodations']}
				</Typography>
			);
		}
		if (total > 0) {
			return (
				<Typography as="p" variant="labelMd">
					<TotalText microCopies={microCopies} total={total} />
				</Typography>
			);
		}
	}, [isLoading, total, classes.totalSkeleton, microCopies]);

	const createMobileSortOptionClickHandler = useCallback((sortOption: SortingOrder) => {
		return () => {
			setMobileSortOption(sortOption);
		};
	}, []);

	return isDesktop ? (
		<div className={classes.filtersControls}>
			<div className={classes.filtersControlsLeft}>{renderControls()}</div>
			<ButtonDropdown
				buttonText={`${microCopies[`search.filtersControls.title`]} ${selectedOption?.name}`}
				buttonVariant="secondary"
				iconType="arrowUpAndDown"
				iconSize="small"
				onOptionClick={handleSortOption}
				options={options}
				selectedValue={selectedOption?.name}
			/>
		</div>
	) : (
		<>
			<ul className={classes.optionsList}>
				{options.map(({ value, label }) => {
					const isSelectedOption = value === mobileSortOption;
					return (
						<li
							key={value}
							className={cx(classes.sortOption, { [classes.selectedSortOption]: isSelectedOption })}
							onClick={createMobileSortOptionClickHandler(value as SortingOrder)}
							data-cy={value}
						>
							{label}
							{isSelectedOption && <Icon type="checkmark" className={classes.checkmark} />}
						</li>
					);
				})}
			</ul>
			<Button
				size="small"
				className={classes.applyButton}
				onClick={handleApplyButtonClick}
				disabled={filters.sort === mobileSortOption}
			>
				{microCopies['common.apply']}
			</Button>
		</>
	);
};

const TotalText: FC<{ total: number; microCopies: MicroCopy }> = ({ total, microCopies }) => {
	if (total === 1) {
		return `${total} ${microCopies['common.accommodation']}`;
	}

	if (total === 500) {
		return inject(microCopies['common.moreAccommodations'], { total: total.toString() }).join('');
	}

	return `${total} ${microCopies['common.accommodations']}`;
};
